import React, { useRef, useState } from 'react';
import { Link, matchPath, useLocation, useSearchParams } from 'react-router-dom';
import { getPathnameOnly } from 'utils/chat-workflow-url';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { MenuOrientation, ThemeDirection, ThemeMode } from 'config';
import useConfig from 'hooks/useConfig';

// third party
import { FormattedMessage, useIntl } from 'react-intl';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ShortcutIcon from '@mui/icons-material/Shortcut';

// types
import { LinkTarget, NavItemType } from 'types';
import { getNestedValue } from 'utils/helpers';
import useLocalization from 'hooks/useLocalization';
import menuItems from 'menu-items';
import { useSelector } from 'store';
import UnreadIndicator from './UnreadIndicator';
import {
  ChatSidebarViewNavTooltipContent,
  ChatSidebarViewNavTooltipTitleOnly,
  chatSidebarNavTooltipSlotProps,
} from './chatSidebarNavTooltip';
import {
  getChatFolderColorTier,
  resolveChatFolderLabel,
  resolveChatViewLabelFromNavItem,
  type ChatFolderTitle,
} from 'utils/chat-view-context-title';
import WorkflowStatusIconBadge from 'views/chats/workflow/WorkflowStatusIconBadge';
import UnreadUsersPreview from './UnreadUsersPreview';
import { Box, Stack } from '@mui/material';
import { appendChatUserToChatViewUrl } from 'utils/chat-workflow-url';
import { appendViewQueryToPath } from 'features/Chat/chat-view-query';
import type { ActiveTabId } from 'utils/chat-sidebar-layout-storage';
import { useViewsPagesVisibilityOptional } from '../ViewsPagesVisibilityContext';
import { isAppChatSidebarConfigurableViewId } from 'utils/default-chat-sidebar-views';
import { isSidebarChatViewVisibleForUser } from 'utils/chat-view-sidebar-visibility';

/** מרווח בין אייקון לטקסט ברשימת תצוגות השיחות (מקביל ל־gap ב־ListItemButton). */
const CHAT_SIDEBAR_BERRY_ICON_TEXT_GAP_PX = 12;

export function collectViewMenuUrls(items: NavItemType[] | undefined): string[] {
  if (!items?.length) return [];
  const out: string[] = [];
  for (const it of items) {
    if (it.type === 'section') continue;
    if (it.url) out.push(it.url);
    if (it.type === 'collapse' && it.children?.length) {
      out.push(...collectViewMenuUrls(it.children));
    }
  }
  return out;
}

export function collectViewMenuIds(items: NavItemType[] | undefined): string[] {
  if (!items?.length) return [];
  const out: string[] = [];
  for (const it of items) {
    if (it.type === 'section') continue;
    if (it.id) out.push(it.id);
    if (it.type === 'collapse' && it.children?.length) {
      out.push(...collectViewMenuIds(it.children));
    }
  }
  return out;
}

/**
 * רשימת שיחות מאוחדת (Berry): רשימה או שיחה פתוחה תחת אותו בסיס — בלי לסמן "כל השיחות" יחד עם wf,
 * ובלי ליפול על מסלולים כמו /snoozed או /chats/unassigned כשהבסיס הוא /chats.
 */
function berryUnifiedPathMatches(itemPathPart: string, pathname: string): boolean {
  const base = itemPathPart.replace(/\/$/, '') || '';
  const loc = pathname.replace(/\/$/, '') || '';
  if (loc === base) return true;
  if (!loc.startsWith(`${base}/`)) return false;
  const rest = loc.slice(base.length + 1);
  const segments = rest.split('/').filter(Boolean);
  if (segments.length !== 1) return false;
  const one = segments[0];
  if (one === 'snoozed') return false;
  if (base === '/chats') {
    const reserved = new Set(['unassigned', 'completed', 'bots', 'custom', 'my', 'not-my']);
    if (reserved.has(one)) return false;
  }
  return true;
}

/** רק שורות «כל השיחות» — URL בלי wf; לא לבלבל עם מסלולים כמו /unassigned /completed שאין להם query בפריט */
function isBerryAllConversationsBasePath(pathPart: string): boolean {
  const p = pathPart.replace(/\/$/, '') || '';
  return p === '/chats/my' || p === '/chats/not-my' || p === '/chats';
}

/** טווח תיקייה מתוך מזהה פריט unified-{scope}-{kind} */
function berryScopeViewIdFromUnifiedItem(itemId: string | undefined): ActiveTabId | null {
  if (!itemId?.startsWith('unified-')) return null;
  if (itemId.startsWith('unified-mine-')) return 'mine';
  if (itemId.startsWith('unified-team-')) return 'team';
  if (itemId.startsWith('unified-all-')) return 'all';
  return null;
}

/** לקישור ListItem — שורת «כל השיחות» מקבלת view בלי wf (אחרת הלינק זהה לעמוד עם wf והלחיצה לא עושה כלום) */
function berryAllConversationsViewIdFromItem(itemId: string | undefined): ActiveTabId | null {
  if (!itemId?.endsWith('-allConversations')) return null;
  return berryScopeViewIdFromUnifiedItem(itemId);
}

/** נתיבים משותפים לכל התיקיות — הבחירה נקבעת לפי `view` ב-query */
function isBerryScopedViewPath(pathPart: string): boolean {
  const p = pathPart.replace(/\/$/, '') || '';
  return p === '/chats/completed' || p === '/chats/unassigned';
}

function berryScopedViewMatches(item: NavItemType, locParams: URLSearchParams): boolean {
  const itemScope = berryScopeViewIdFromUnifiedItem(item.id);
  if (!itemScope) return true;
  const locView = locParams.get('view');
  if (locView) return locView === itemScope;
  return itemScope === 'all';
}

export function menuUrlMatchesLocation(
  item: NavItemType,
  pathname: string,
  search: string,
  isChatsViewsDirectLink: boolean,
  berryChatSidebarItem?: boolean,
): boolean {
  const raw = item.link || item.url;
  if (!raw) return false;
  const pathPart = getPathnameOnly(raw);
  const qIdx = raw.indexOf('?');
  const queryPart = qIdx >= 0 ? raw.slice(qIdx + 1) : '';

  if (item.id === 'chats') {
    const pathMatch = !!matchPath({ path: pathPart, end: false }, pathname);
    return pathMatch && !isChatsViewsDirectLink;
  }

  const locParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

  if (berryChatSidebarItem) {
    if (!berryUnifiedPathMatches(pathPart, pathname)) return false;
    if (queryPart) {
      const itemParams = new URLSearchParams(queryPart);
      for (const [k, v] of itemParams.entries()) {
        if (locParams.get(k) !== v) return false;
      }
      return true;
    }
    if (isBerryAllConversationsBasePath(pathPart) && (locParams.has('wf') || locParams.has('sendFailed'))) return false;
    if (isBerryScopedViewPath(pathPart) && !berryScopedViewMatches(item, locParams)) return false;
    return true;
  }

  const pathMatch = !!matchPath({ path: pathPart, end: false }, pathname);
  if (!pathMatch) return false;

  if (queryPart) {
    const itemParams = new URLSearchParams(queryPart);
    for (const [k, v] of itemParams.entries()) {
      if (locParams.get(k) !== v) return false;
    }
    return true;
  }

  return true;
}

export function computeIsChatsViewsDirectLink(pathname: string): boolean {
  const viewsPages = menuItems.items.find((i) => i.id === 'views-pages');
  return (
    collectViewMenuUrls(viewsPages?.children)
      .filter((url) => getPathnameOnly(url) !== '/chats')
      .some((url) => !!matchPath({ path: getPathnameOnly(url), end: false }, pathname)) ||
    !!matchPath({ path: '/chats/custom', end: false }, pathname)
  );
}

/** קישור ListItem לשורות סרגל Berry — לוגיקה זהה ל-useMemo בתוך NavItem */
export function buildBerryUnifiedNavLinkTo(
  item: NavItemType,
  searchParams: URLSearchParams,
  selectedChatUserId: string | undefined,
  isViewPage: boolean,
  berryChatSidebarItem: boolean,
): string {
  const base = item.link || item.url!;
  const allConvView = berryChatSidebarItem ? berryAllConversationsViewIdFromItem(item.id) : null;
  if (allConvView) {
    const withView = appendViewQueryToPath(base, allConvView);
    return appendChatUserToChatViewUrl(withView, selectedChatUserId && isViewPage ? selectedChatUserId : null);
  }
  const pathOnly = getPathnameOnly(base);
  if (berryChatSidebarItem && isBerryScopedViewPath(pathOnly)) {
    const scopeView = berryScopeViewIdFromUnifiedItem(item.id);
    if (scopeView) {
      const withView = appendViewQueryToPath(base, scopeView);
      return appendChatUserToChatViewUrl(withView, selectedChatUserId && isViewPage ? selectedChatUserId : null);
    }
  }
  if (base.includes('?')) {
    return appendChatUserToChatViewUrl(base, selectedChatUserId && isViewPage ? selectedChatUserId : null);
  }
  const wf = searchParams.get('wf');
  const withWf = wf ? `${base}?wf=${encodeURIComponent(wf)}` : base;
  return appendChatUserToChatViewUrl(withWf, selectedChatUserId && isViewPage ? selectedChatUserId : null);
}

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

interface NavItemProps {
  item: NavItemType;
  level: number;
  isParents?: boolean;
  setSelectedID?: () => void;
  /** מונה unread לפי טאב/סטטוס (סרגל שיחות מאוחד) */
  derivedUnreadCount?: number;
  /** מונה סך-הכול — מוצג ב-tooltip בלבד */
  derivedTotalCount?: number;
  /** מונה שיחות ב«ממתינה לאיש קשר» שההודעה האחרונה לא נשלחה */
  derivedSendFailedCount?: number;
  sendFailedFilterActive?: boolean;
  sendFailedFilterTo?: string;
  /** עיצוב Berry לרשימת סטטוסים בסרגל שיחות */
  berryChatSidebarItem?: boolean;
  /** מחושב ב-UnifiedRow — מפחית re-renders כשמשנים רק שורה אחרת */
  berryOptimized?: { isSelected: boolean; to: string };
  /** תיקיית הסרגל שמכילה את השורה — לכותרת טולטיפ (תיקייה ◂ תצוגה) */
  chatSidebarFolderTitle?: ChatFolderTitle;
}

function NavItemInner({
  item,
  level,
  isParents = false,
  setSelectedID,
  derivedUnreadCount,
  derivedTotalCount,
  derivedSendFailedCount,
  sendFailedFilterActive,
  sendFailedFilterTo,
  berryChatSidebarItem = false,
  berryOptimized,
  chatSidebarFolderTitle,
}: NavItemProps) {
  const theme = useTheme();
  const { currentLocale } = useLocalization();
  const intl = useIntl();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const ref = useRef<HTMLSpanElement>(null);

  const selectedChatUserId = useSelector((state) => state.chat.selectedChatUser?.UserDisplayPhoneNumber);

  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  const { mode, menuOrientation, borderRadius, themeDirection } = useConfig();

  const showUnreadPreviewOnHover = useSelector((state) => state.userConfig.showUnreadPreviewOnHover ?? true);
  const currentUser = useSelector((state) => state.user);
  const defaultViewHiddenMap = useSelector((state) => state.userConfig.defaultChatViewHiddenForApplicationUserUdids);
  const viewsPagesVisibility = useViewsPagesVisibilityOptional();
  const canManageDefaultViewVisibility = currentUser?.Role === 'admin';

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  const [isBadge, setIsBadge] = useState(drawerOpen);
  const [unreadUsersAnchorEl, setUnreadUsersAnchorEl] = React.useState<HTMLElement | null>(null);

  //* Custom check for chats different views in case of displaying correct selected items
  const viewsPages = menuItems.items.find((item) => item.id === 'views-pages');
  const isChatsViewsDirectLink =
    berryOptimized && berryChatSidebarItem ? false : computeIsChatsViewsDirectLink(pathname);

  const isViewPage = collectViewMenuIds(viewsPages?.children).includes(item.id!);

  const isSelected =
    berryOptimized && berryChatSidebarItem
      ? berryOptimized.isSelected
      : menuUrlMatchesLocation(item, pathname, search, isChatsViewsDirectLink, berryChatSidebarItem);

  const navLinkTo = React.useMemo(() => {
    if (berryOptimized && berryChatSidebarItem) return berryOptimized.to;
    return buildBerryUnifiedNavLinkTo(item, searchParams, selectedChatUserId, isViewPage, berryChatSidebarItem);
  }, [
    berryOptimized,
    berryChatSidebarItem,
    item,
    searchParams,
    selectedChatUserId,
    isViewPage,
  ]);

  // const [hoverStatus, setHover] = useState<boolean>(false);

  // const compareSize = () => {
  //   const compare = ref.current && ref.current.scrollWidth > ref.current.clientWidth;
  //   setHover(compare as boolean);
  // };

  // useEffect(() => {
  //   compareSize();
  //   window.addEventListener('resize', compareSize);
  //   window.removeEventListener('resize', compareSize);
  // }, []);

  const Icon = item?.icon as React.ComponentType<{ stroke?: number; size?: string; style?: React.CSSProperties }>;
  const itemIcon =
    item.workflowStatusId != null ? (
      <WorkflowStatusIconBadge status={item.workflowStatusId} size={drawerOpen ? 20 : 22} />
    ) : item?.icon ? (
      React.isValidElement(item.icon) ? (
        item.icon
      ) : (
        <Icon
          stroke={1.5}
          size={'24px'}
          style={{ ...(isHorizontal && isParents && { fontSize: 24, stroke: '1.5' }) }}
        />
      )
    ) : (
      <FiberManualRecordIcon
        sx={{ width: isSelected ? 8 : 6, height: isSelected ? 8 : 6 }}
        fontSize={level > 0 ? 'inherit' : 'medium'}
      />
    );

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const itemHandler = React.useCallback(() => {
    if (downMD) handlerDrawerOpen(false);

    if (isParents && setSelectedID) {
      setSelectedID();
    }
  }, [downMD, isParents, setSelectedID]);

  const iconSelectedColor = mode === ThemeMode.DARK && drawerOpen ? 'text.primary' : 'secondary.main';

  React.useEffect(() => {
    if (drawerOpen) {
      setTimeout(() => {
        setIsBadge(false);
      }, 300);
    } else {
      setIsBadge(true);
    }
  }, [drawerOpen]);

  const hasDefaultViewIconAction =
    Boolean(canManageDefaultViewVisibility && viewsPagesVisibility && item.id && isAppChatSidebarConfigurableViewId(item.id));
  const hasCustomViewIconAction = Boolean(item.onViewIconDoubleClick);
  /** תצוגות מותאמות לא ב־collectViewMenuIds — רק לפי callback */
  const showViewIconAsAction = Boolean(hasCustomViewIconAction || (isViewPage && hasDefaultViewIconAction));

  const handleViewIconDoubleClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (item.onViewIconDoubleClick) {
        item.onViewIconDoubleClick(e);
        return;
      }
      if (canManageDefaultViewVisibility && viewsPagesVisibility && item.id && isAppChatSidebarConfigurableViewId(item.id)) {
        viewsPagesVisibility.openDefaultViewVisibilityDialog(item.id);
      }
    },
    [item, canManageDefaultViewVisibility, viewsPagesVisibility],
  );

  const viewIconActionButtonSx = React.useMemo(
    () =>
      showViewIconAsAction
        ? {
            cursor: 'pointer',
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.09),
              boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.28)}`,
            },
            '&:active': {
              transform: 'scale(0.94)',
            },
            ...(drawerOpen &&
              level === 1 && {
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 46,
                height: 46,
                minWidth: 46,
                flexShrink: 0,
                boxSizing: 'border-box',
              }),
          }
        : {},
    [showViewIconAsAction, theme, drawerOpen, level],
  );

  /** רשימת תצוגות בשיחות: פריסה אחידה — לא מגדירים direction ב-sx (stylis-plugin-rtl). */
  const isChatSidebarBerryRow = Boolean(berryChatSidebarItem && drawerOpen && level === 1);

  const chatSidebarBerryListItemButtonSx = React.useMemo(() => {
    if (!isChatSidebarBerryRow) return undefined;
    /** כמו `MuiListItemButton` ב־compStyleOverride: רקע secondary ללא פס צד */
    const menuSelectedBack =
      theme.palette.mode === 'dark' ? alpha(theme.palette.secondary.main, 0.15) : theme.palette.secondary.light;
    const menuSelected =
      theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark;
    return {
      /** כמו Berry (`NavItem` + `MuiListItemButton` ב־compStyleOverride: py ~10px) — לא לדחוס עם minHeight קבוע */
      mb: 0.5,
      marginInline: '8px',
      borderRadius: 1,
      gap: `${CHAT_SIDEBAR_BERRY_ICON_TEXT_GAP_PX}px`,
      alignItems: 'center',
      transition: theme.transitions.create(['background-color', 'color'], {
        duration: 100,
        easing: theme.transitions.easing.easeOut,
      }),
      '&:hover': {
        bgcolor: `${alpha(theme.palette.grey[500], theme.palette.mode === 'dark' ? 0.14 : 0.1)} !important`,
      },
      '&.Mui-selected': {
        bgcolor: `${menuSelectedBack} !important`,
        color: menuSelected,
        boxShadow: 'none',
        borderRadius: '8px',
        '& .MuiListItemIcon-root': {
          color: menuSelected,
        },
        '&:hover': {
          bgcolor: `${menuSelectedBack} !important`,
          color: menuSelected,
          '& .MuiListItemIcon-root': {
            color: menuSelected,
          },
        },
      },
    };
  }, [isChatSidebarBerryRow, theme]);

  const chatSidebarBerryListItemTextSx = React.useMemo(() => {
    if (!isChatSidebarBerryRow) return undefined;
    return {
      flex: '1 1 auto',
      minWidth: 0,
      my: 0,
    };
  }, [isChatSidebarBerryRow]);

  const listItemPrimaryTypographySx = React.useMemo(
    () =>
      isChatSidebarBerryRow
        ? {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
            maxWidth: '100%',
            textAlign: 'start',
          }
        : {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: 102,
            maxWidth: '100%',
            ...(themeDirection === ThemeDirection.RTL && { textAlign: 'end', direction: 'rtl' }),
          },
    [isChatSidebarBerryRow, themeDirection],
  );

  if (
    isViewPage &&
    item.id &&
    isAppChatSidebarConfigurableViewId(item.id) &&
    !isSidebarChatViewVisibleForUser(item.id, currentUser?.Udid, defaultViewHiddenMap)
  ) {
    return null;
  }

  /** שורות עם מונים: תצוגות מערכת (isViewPage) או כל שורה שקיבלה מונה מחושב (תצוגות מותאמות) */
  const hasCounters = derivedUnreadCount != null || derivedTotalCount != null;
  const showCounters = isViewPage || hasCounters;
  const unreadCount = derivedUnreadCount ?? 0;
  const hasViewCounts = derivedTotalCount != null;
  const showUnreadCounter = showCounters && unreadCount > 0;

  const collapsedName =
    item?.customPage && item.title
      ? getNestedValue(currentLocale, item.title)
      : item.noTranslation
        ? item.title
        : item.title
          ? intl.formatMessage({ id: item.title, defaultMessage: item.title })
          : '';
  const chatSidebarFolderLabel = chatSidebarFolderTitle
    ? resolveChatFolderLabel(chatSidebarFolderTitle, intl)
    : undefined;
  const chatSidebarViewLabel =
    berryChatSidebarItem && level === 1 ? resolveChatViewLabelFromNavItem(item, intl) : undefined;
  const chatSidebarFolderTier = chatSidebarFolderTitle
    ? getChatFolderColorTier(chatSidebarFolderTitle)
    : 'other';

  const chatSidebarNavTooltip =
    berryChatSidebarItem &&
    level === 1 &&
    chatSidebarFolderTitle &&
    chatSidebarFolderLabel &&
    chatSidebarViewLabel
      ? hasViewCounts && derivedTotalCount != null
        ? (
            <ChatSidebarViewNavTooltipContent
              folderLabel={chatSidebarFolderLabel}
              viewLabel={chatSidebarViewLabel}
              folderTier={chatSidebarFolderTier}
              unread={unreadCount}
              total={derivedTotalCount}
            />
          )
        : (
            <ChatSidebarViewNavTooltipTitleOnly
              folderLabel={chatSidebarFolderLabel}
              viewLabel={chatSidebarViewLabel}
              folderTier={chatSidebarFolderTier}
            />
          )
      : null;

  const showChatSidebarNavTooltip = Boolean(chatSidebarNavTooltip);

  const navTooltipTitle = showChatSidebarNavTooltip
    ? chatSidebarNavTooltip
    : !drawerOpen && item.title
      ? (
          <Stack spacing={0.75} sx={{ py: 0.25 }}>
            <Typography component="span" variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
              {collapsedName}
            </Typography>
          </Stack>
        )
      : drawerOpen && item.viewTooltipId
        ? intl.formatMessage({ id: item.viewTooltipId })
        : '';

  const navTooltipSlotProps = showChatSidebarNavTooltip ? chatSidebarNavTooltipSlotProps : undefined;
  const navTooltipDisableHover = showChatSidebarNavTooltip ? false : drawerOpen && !item.viewTooltipId;

  return (
    <>
      {!isHorizontal ? (
        <Tooltip
          title={navTooltipTitle}
          placement="left"
          arrow
          slotProps={navTooltipSlotProps}
          disableHoverListener={navTooltipDisableHover}
        >
        <ListItemButton
          component={Link}
          to={navLinkTo}
          target={itemTarget}
          disabled={item.disabled}
          disableRipple
          dir={isChatSidebarBerryRow ? 'rtl' : undefined}
          sx={{
            /** z-index גבוה רק מחוץ לשורות Berry של סרגל השיחות — אחרת השורות מצוירות מעל בלוק «תצוגות מותאמות» */
            ...(isChatSidebarBerryRow ? {} : { zIndex: 1201 }),
            borderRadius: `${borderRadius}px`,
            mb: 0.5,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'transparent',
            },
            ...(drawerOpen && level !== 1 && { ml: `${level * 18}px` }),
            ...(!drawerOpen && { pl: 1.25 }),
            ...(drawerOpen &&
              level === 1 &&
              mode !== ThemeMode.DARK && {
                '&:hover': {
                  bgcolor: 'secondary.light',
                },
                '&.Mui-selected': {
                  bgcolor: 'secondary.light',
                  color: iconSelectedColor,
                  '&:hover': {
                    color: iconSelectedColor,
                    bgcolor: 'secondary.light',
                  },
                },
              }),
            ...((!drawerOpen || level !== 1) && {
              py: level === 1 ? 0 : 1,
              '&:hover': {
                bgcolor: 'transparent',
              },
              '&.Mui-selected': {
                '&:hover': {
                  bgcolor: 'transparent',
                },
                bgcolor: 'transparent',
              },
            }),
            ...(item.actionHoverOnly && {
              '&:hover .nav-item-action': {
                opacity: 1,
              },
            }),
            ...(chatSidebarBerryListItemButtonSx ?? {}),
          }}
          selected={isSelected}
          onClick={() => itemHandler()}
          onMouseEnter={(e) => isViewPage && setUnreadUsersAnchorEl(e.currentTarget)}
          onMouseLeave={() => isViewPage && setUnreadUsersAnchorEl(null)}
        >
          <UnreadIndicator
            asBadge={true}
            menuItemId={item.id}
            counterTone={item.counterTone}
            visible={showUnreadCounter && !drawerOpen}
            countOverride={derivedUnreadCount}
            badgeVerticallyCentered={Boolean(berryChatSidebarItem && drawerOpen && showUnreadCounter)}
          >
            <ButtonBase
              component="span"
              aria-label={
                showViewIconAsAction
                  ? intl.formatMessage({ id: 'chat.sidebarCustomViews.viewIconAriaLabel' })
                  : 'theme-icon'
              }
              title={
                showViewIconAsAction
                  ? intl.formatMessage({ id: 'chat.sidebarCustomViews.viewIconDoubleClickHint' })
                  : undefined
              }
              onDoubleClick={showViewIconAsAction ? handleViewIconDoubleClick : undefined}
              sx={{
                borderRadius: `${borderRadius}px`,
                ...viewIconActionButtonSx,
              }}
              disableRipple={drawerOpen}
            >
              <ListItemIcon
                sx={{
                  minWidth: level === 1 ? 36 : 18,
                  color: isSelected ? iconSelectedColor : 'text.primary',
                  ...(isChatSidebarBerryRow &&
                    !showViewIconAsAction && {
                      flexShrink: 0,
                      width: 36,
                      minWidth: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }),
                  ...(!drawerOpen &&
                    level === 1 && {
                      borderRadius: `${borderRadius}px`,
                      width: 46,
                      height: 46,
                      alignItems: 'center',
                      justifyContent: 'center',
                      ...(!showViewIconAsAction && {
                        '&:hover': {
                          bgcolor:
                            mode === ThemeMode.DARK
                              ? alpha(theme.palette.secondary.main, 0.25)
                              : 'secondary.light',
                        },
                        ...(isSelected && {
                          bgcolor:
                            mode === ThemeMode.DARK
                              ? alpha(theme.palette.secondary.main, 0.25)
                              : 'secondary.light',
                          '&:hover': {
                            bgcolor:
                              mode === ThemeMode.DARK
                                ? alpha(theme.palette.secondary.main, 0.3)
                                : 'secondary.light',
                          },
                        }),
                      }),
                    }),
                  ...(drawerOpen &&
                    level === 1 &&
                    showViewIconAsAction && {
                      minWidth: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                    }),
                }}
              >
                {itemIcon}
              </ListItemIcon>
            </ButtonBase>
          </UnreadIndicator>
          {(drawerOpen || (!drawerOpen && level !== 1)) && (
            // <Tooltip
            //   title={
            //     item.noTranslation ? (
            //       item.title
            //     ) : item?.customPage && item.title ? (
            //       getNestedValue(currentLocale, item.title)
            //     ) : (
            //       <FormattedMessage id={item.title} />
            //     )
            //   }
            //   disableHoverListener={!hoverStatus}
            // >
            <ListItemText
              sx={chatSidebarBerryListItemTextSx}
              primary={
                item.noTranslation ? (
                  item.title
                ) : item?.customPage && item.title ? (
                  getNestedValue(currentLocale, item.title)
                ) : isChatSidebarBerryRow && item.globalQueueShortcut ? (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      minWidth: 0,
                      maxWidth: '100%',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <FormattedMessage id={item.title} />
                    </Box>
                    <ShortcutIcon
                      sx={{
                        fontSize: 14,
                        opacity: 0.38,
                        flexShrink: 0,
                        color: 'text.secondary',
                      }}
                      aria-hidden
                    />
                  </Box>
                ) : (
                  <FormattedMessage id={item.title} />
                )
              }
              primaryTypographyProps={{
                ref,
                noWrap: true,
                variant: isSelected ? 'h5' : 'body1',
                color: 'inherit',
                sx: listItemPrimaryTypographySx,
              }}
              secondary={
                item.caption && (
                  <Typography
                    variant="caption"
                    gutterBottom
                    sx={{ display: 'block', ...theme.typography.subMenuCaption }}
                  >
                    {item?.customPage && item.caption ? (
                      getNestedValue(currentLocale, item.caption)
                    ) : (
                      <FormattedMessage id={item.caption} />
                    )}
                  </Typography>
                )
              }
            />
            // </Tooltip>
          )}
          {drawerOpen && item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            />
          )}
          {showCounters && drawerOpen && !isBadge && (showUnreadCounter || (derivedSendFailedCount != null && derivedSendFailedCount > 0)) && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, ml: 0.5, flexShrink: 0 }}>
              {derivedSendFailedCount != null && derivedSendFailedCount > 0 && sendFailedFilterTo && (
                <Tooltip
                  title={intl.formatMessage({
                    id: 'sidebar.nav.views.sendFailedFilterTooltip',
                    defaultMessage: 'הודעה אחרונה שלא נשלחה — לחץ לסינון',
                  })}
                >
                  <Chip
                    component={Link}
                    to={sendFailedFilterTo}
                    clickable
                    size="small"
                    label={derivedSendFailedCount}
                    color="error"
                    variant={sendFailedFilterActive ? 'filled' : 'outlined'}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    sx={{
                      height: 20,
                      minWidth: 24,
                      flexShrink: 0,
                      '& .MuiChip-label': {
                        px: 0.75,
                        fontSize: 11,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                      },
                    }}
                  />
                </Tooltip>
              )}
              {showUnreadCounter && (
                <Tooltip
                  title={showChatSidebarNavTooltip ? chatSidebarNavTooltip : navTooltipTitle}
                  placement="left"
                  arrow
                  slotProps={showChatSidebarNavTooltip ? chatSidebarNavTooltipSlotProps : navTooltipSlotProps}
                  describeChild
                >
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <UnreadIndicator
                      menuItemId={item.id}
                      counterTone={item.counterTone}
                      countOverride={derivedUnreadCount}
                      badgeVerticallyCentered={Boolean(berryChatSidebarItem)}
                    />
                  </Box>
                </Tooltip>
              )}
            </Box>
          )}
          {isViewPage && showUnreadPreviewOnHover && (
            <UnreadUsersPreview
              menuItemId={item.id}
              open={Boolean(unreadUsersAnchorEl)}
              anchorEl={unreadUsersAnchorEl}
              onClose={() => setUnreadUsersAnchorEl(null)}
            />
          )}

          {item.action && (
            <Box
              className="nav-item-action"
              sx={{
                opacity: item.actionHoverOnly ? 0 : 1,
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              {item.action}
            </Box>
          )}
        </ListItemButton>
        </Tooltip>
      ) : (
        <Tooltip
          title={navTooltipTitle}
          placement="left"
          arrow
          slotProps={navTooltipSlotProps}
          disableHoverListener={navTooltipDisableHover}
        >
        <ListItemButton
          component={Link}
          to={navLinkTo}
          target={itemTarget}
          disabled={item.disabled}
          disableRipple={isViewPage}
          sx={{
            cursor: 'pointer',
            borderRadius: isParents ? `${borderRadius}px` : 0,
            mb: isParents ? 0 : 0.5,
            alignItems: 'flex-start',
            backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
            py: 1,
            pl: 2,
            mr: isParents ? 1 : 0,
          }}
          selected={isSelected}
          onClick={() => itemHandler()}
        >
          {showViewIconAsAction ? (
            <ButtonBase
              component="span"
              aria-label={intl.formatMessage({ id: 'chat.sidebarCustomViews.viewIconAriaLabel' })}
              title={intl.formatMessage({ id: 'chat.sidebarCustomViews.viewIconDoubleClickHint' })}
              onDoubleClick={handleViewIconDoubleClick}
              sx={{
                my: 'auto',
                borderRadius: `${borderRadius}px`,
                ...viewIconActionButtonSx,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: !item?.icon ? 18 : 36,
                  ...(drawerOpen &&
                    level === 1 &&
                    showViewIconAsAction && {
                      minWidth: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                    }),
                }}
              >
                {itemIcon}
              </ListItemIcon>
            </ButtonBase>
          ) : (
            <ListItemIcon
              sx={{
                my: 'auto',
                minWidth: !item?.icon ? 18 : 36,
              }}
            >
              {itemIcon}
            </ListItemIcon>
          )}

          <ListItemText
            sx={{ mb: 0.25 }}
            primary={
              <Typography variant={isSelected ? 'h5' : 'body1'} color="inherit">
                {item?.customPage && item.title ? getNestedValue(currentLocale, item.title) : item.title}
              </Typography>
            }
            secondary={
              item.caption && (
                <Typography
                  variant="caption"
                  gutterBottom
                  sx={{ display: 'block', ...theme.typography.subMenuCaption }}
                >
                  {item?.customPage && item.caption
                    ? getNestedValue(currentLocale, item.caption)
                    : item.caption}
                </Typography>
              )
            }
          />

          {item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            />
          )}
        </ListItemButton>
        </Tooltip>
      )}
    </>
  );
}

function chatSidebarFolderTitleEqual(a?: ChatFolderTitle, b?: ChatFolderTitle): boolean {
  if (a === b) return true;
  if (!a || !b) return !a && !b;
  if (a.type !== b.type) return false;
  if (a.type === 'text' && b.type === 'text') return a.text === b.text;
  if (a.type === 'locale' && b.type === 'locale') return a.id === b.id;
  return false;
}

function navItemPropsAreEqual(prev: NavItemProps, next: NavItemProps): boolean {
  if (!prev.berryChatSidebarItem || !next.berryChatSidebarItem) return false;
  if (!prev.berryOptimized || !next.berryOptimized) return false;
  if (prev.level !== next.level) return false;
  if (prev.item.id !== next.item.id) return false;
  if (!chatSidebarFolderTitleEqual(prev.chatSidebarFolderTitle, next.chatSidebarFolderTitle)) return false;
  if (prev.isParents !== next.isParents) return false;
  if (prev.derivedUnreadCount !== next.derivedUnreadCount) return false;
  if (prev.derivedTotalCount !== next.derivedTotalCount) return false;
  if (prev.derivedSendFailedCount !== next.derivedSendFailedCount) return false;
  if (prev.sendFailedFilterActive !== next.sendFailedFilterActive) return false;
  if (prev.sendFailedFilterTo !== next.sendFailedFilterTo) return false;
  return (
    prev.berryOptimized.isSelected === next.berryOptimized.isSelected &&
    prev.berryOptimized.to === next.berryOptimized.to
  );
}

const NavItem = React.memo(NavItemInner, navItemPropsAreEqual);
export default NavItem;
